import { COLUMN_LABELS } from 'src/components/Plans/utils/constants'

export enum planCategories {
  APPROVED_REGIONAL_PLAN = 'approved regional plan',
  COMMUNITY = 'community',
  CORE = 'core',
  HIGH_VALUE_PLAN = 'high value plan',
}

export const PlanCategoryLabels: Record<planCategories, string> = {
  [planCategories.APPROVED_REGIONAL_PLAN]: 'Approved Regional Plan',
  [planCategories.COMMUNITY]: 'Community',
  [planCategories.CORE]: 'Core',
  [planCategories.HIGH_VALUE_PLAN]: 'High Value Plan',
}

export const getPlanCategoriesDisplayKeyValue = (
  order: planCategories[]
): { label: string; value: planCategories }[] =>
  order.map((key) => ({
    label: PlanCategoryLabels[key],
    value: key,
  }))

export const planCategoriesForRadioGroup = (isCorporateUser: boolean) => {
  if (!isCorporateUser) {
    return getPlanCategoriesDisplayKeyValue([
      planCategories.HIGH_VALUE_PLAN,
      planCategories.COMMUNITY,
    ])
  }
  return getPlanCategoriesDisplayKeyValue([
    planCategories.CORE,
    planCategories.HIGH_VALUE_PLAN,
    planCategories.COMMUNITY,
  ])
}

export const planCategoriesForDropdown = getPlanCategoriesDisplayKeyValue([
  planCategories.COMMUNITY,
  planCategories.CORE,
  planCategories.HIGH_VALUE_PLAN,
  planCategories.APPROVED_REGIONAL_PLAN,
])

export enum PlanPropertyKeys {
  PLAN_CATEGORY = 'planCategory',
  PLAN_OVERVIEW = 'planOverview',
}

export enum familyTypes {
  MULTI = 'multi',
  MULTI_FAMILY = 'MULTI_FAMILY',
  SINGLE = 'single',
}

export enum classTypes {
  SHELL = 'Shell',
  SINGLE = 'Single',
  UNIT = 'Unit',
}

// Local Storage Keys for visible filters (per plan type)
export const VISIBLE_FILTERS_KEYS = {
  [classTypes.SHELL]: 'visibleFilters_shell',
  [classTypes.SINGLE]: 'visibleFilters_single',
  [classTypes.UNIT]: 'visibleFilters_unit',
}

// Session Storage Keys for filter values (per plan type)
export const FILTER_VALUES_KEYS = {
  [classTypes.SHELL]: 'filterValues_shell',
  [classTypes.SINGLE]: 'filterValues_single',
  [classTypes.UNIT]: 'filterValues_unit',
}

// Session Storage Keys for comparison attributes (per plan type)
export const COMPARISON_ATTRIBUTES_KEYS = {
  [classTypes.SHELL]: 'comparisonAttributes_shell',
  [classTypes.SINGLE]: 'comparisonAttributes_single',
  [classTypes.UNIT]: 'comparisonAttributes_unit',
}

export const SORT_STORAGE_KEYS = {
  [classTypes.SHELL]: {
    groupBy: 'shell_groupBy',
    sortBy: 'shell_sortBy',
    sortOrder: 'shell_sortOrder',
  },
  [classTypes.SINGLE]: {
    groupBy: 'plan_groupBy',
    sortBy: 'plan_sortBy',
    sortOrder: 'plan_sortOrder',
  },
  [classTypes.UNIT]: {
    groupBy: 'unit_groupBy',
    sortBy: 'unit_sortBy',
    sortOrder: 'unit_sortOrder',
  },
}

export const PLAN_TYPE = 'plan-type'
export const SINGLE_FAMILY = 'single-family'
export const DASHBOARD = 'dashboard'
export const MULTI_FAMILY = 'multi-family'
export const FAVORITES = 'favorites'
export const PENDING_APPROVALS = 'pending-approvals'
export const REGIONAL_APPROVALS = 'regional-approvals'
export const EXPORTS = 'exports'
export const PLAN_CATEGORY = 'Plan Category'
export const PENDING_PLANS = 'Pending Plans'

export const HIDE_UNHIDE_ACTION = 'hide/unhide'

export enum structureTypes {
  CARPORT = 'carport',
  CONDOMINIUM = 'condominium',
  GARAGE = 'garage',
  TOWN_HOME = 'townhome',
}

export enum buildingTypes {
  SHELL = 'shell',
  UNIT = 'unit',
}

export enum autoFillTypes {
  MAX = 'max',
  MIN = 'min',
}

export enum ParentTypeEnum {
  CHILD = 'child',
  GRANDCHILD = 'grandchild',
  PARENT = 'parent',
}

export const maxSquareFootLength = 20

/* eslint-disable perfectionist/sort-enums */

export enum TABS {
  PLAN_MASTER_DATA = 0,
  ADDITIONAL_PLAN_ATTRIBUTES = 1,
  ADDITIONAL_OPTIONS = 2,
  SUPPLEMENTAL_DOCS_SPECS = 3,
  REVIEW_TAB = 4,
}

/* eslint-enable perfectionist/sort-enums */
export const subtitle1 = 'subtitle1'
export const fontFamilyKarla = `'Karla', sans-serif`
export const livingSpaceImpacts = 'Living Space Impacts'

export const filesAllowedToView = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'application/pdf',
  'image/svg+xml',
]

export const mimeToExtension: Record<string, string> = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/svg+xml': '.svg',
}

export const allowedImageTypes = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
]

export const fileMimeType = {
  jpeg: 'image/jpeg',
  pdf: 'application/pdf',
  svg: 'image/svg+xml',
}

export const fileCannotBeViewed = 'This file cannot be viewed.'

export const dateTimeFormatForFileUploads = 'MMM DD YYYY HH:mm:ss'

export enum fieldNames {
  ADDITIONAL_BATHROOMS = 'additionalBathrooms',
  ADDITIONAL_ROOMS = 'additionalRooms',
  APPROVAL_STATUS = 'approvalStatus',
  ARCHITECTURAL_SHAKE = 'architecturalShake',
  ARCHITECTURAL_SHINGLE = 'architecturalShingle',
  ARCHITECTURE_FIRM = 'architecturalFirm',
  BASE_HOUSE_PRE = 'baseHousePre',
  BASE_HOUSE_PRICE = 'baseHomePrice', // we are treating this as baseHousePrice in frontend and baseHomePrice in backend
  BASE_HOUSE_PRICE_FOR_PLAN_MASTER_DATA = 'baseHousePrice', // This is a temporary fix until we align backend and frontend naming conventions
  BASE_PLAN = 'basePlan',
  BASEMENT = 'basement',
  BASEMENT_SQ_FT = 'basementSqft',
  BASEMENT_TYPE = 'basementType',
  BATHROOMS = 'bathrooms',
  BEDROOMS = 'bedrooms',
  BOARD_AND_BATTEN = 'boardAndBatten',
  BONUS_ROOM = 'bonusRoom',
  BONUS_ROOM_LIVING_SPACE_IMPACTS = 'bonusRoomLivingSpaceImpacts',
  BONUS_ROOM_LOCATION = 'bonusRoomLocation',
  BONUS_ROOM_SQ_FT = 'bonusRoomSqft',
  BYPASS_CORE_PLAN_VALIDATION = 'bypassCorePlanValidation',
  COMMUNITY_ID = 'communityId',
  COMMUNITY_NAME = 'communityName',
  COMMUNITY_NUMBER = 'communityNumber',
  COPY_APPROVAL = 'copyApproval',
  CORE_PLAN = 'corePlan',
  CREATED_ON_DATE = 'createdOnDate',
  CUSTOMER_SEGMENT = 'customerSegment',
  DECK = 'deck',
  DECK_SIZE_SQ_FT = 'deckSize',
  DEPTH_FT = 'depthOfThePlanInFeet',
  DEPTH_IN = 'depthOfThePlanInInch',
  DEPTH_OF_PLAN = 'depthOfPlan',
  DEPTH_UNIT_FT = 'depthOfTheUnitInFeet',
  DEPTH_UNIT_IN = 'depthOfTheUnitInInch',
  DERIVED_FROM_APPROVED_REGIONAL_PLAN = 'derivedFromApprovedRegionalPlan',
  DERIVED_FROM_CORE = 'derivedFromCore',
  DIVISION = 'division',
  EFFECTIVE_DATE = 'effectiveDate',
  EIGHTH_FLOOR_SQ_FT = 'eighthFloorSqft',
  ENGINEER_NAME = 'engineerName',
  ENGINEER_OF_RECORD = 'engineerOfRecord',
  ENTRY = 'entry',
  ENTRY_SIZE_SQ_FT = 'entrySqft',
  EXPORT_FLOOR_PLANS = 'exportFloorPlans',
  EXTERIOR_CLADDING = 'exteriorCladding',
  EXTERIOR_WALL_CONSTRUCTION = 'exteriorWallConstruction',
  FAMILY_ROOM = 'familyRoom',
  FAMILY_ROOM_LIVING_SPACE_IMPACTS = 'familyRoomLivingSpaceImpacts',
  FAMILY_ROOM_LOCATION = 'familyRoomLocation',
  FAMILY_ROOM_SQ_FT = 'familyRoomSqft',
  FIFTH_FLOOR_SQ_FT = 'fifthFloorSqft',
  FIRM_NAME = 'firmName',
  FIRST_FLOOR_SQ_FT = 'firstFloorSqft',
  FLEX = 'flex',
  FLEX_LIVING_SPACE_IMPACTS = 'flexLivingSpaceImpacts',
  FLEX_LOCATION = 'flexLocation',
  FLEX_SQ_FT = 'flexSqft',
  FLOOR_CEILING_HEIGHT_LIST = 'floorCeilingHeightList',
  FORMAL_DINING_ROOM = 'formalDiningRoom',
  FORMAL_LIVING_ROOM = 'formalLivingRoom',
  FOUNDATION_TYPE = 'foundationType',
  FOURTH_FLOOR_SQ_FT = 'fourthFloorSqft',
  FULL_BATHROOMS = 'fullBathrooms',
  GAME_ROOM = 'gameRoom',
  GAME_ROOM_LIVING_SPACE_IMPACTS = 'gameRoomLivingSpaceImpacts',
  GAME_ROOM_LOCATION = 'gameRoomLocation',
  GAME_ROOM_SQ_FT = 'gameRoomSqft',
  GARAGE_ALIGNMENT = 'garageAlignment',
  GARAGE_ATT_DET = 'garageType',
  GARAGE_LOAD = 'garageLoad',
  GARAGE_SIZE_2 = 'garageSize2',
  GARAGE_SIZE_SQ_FT = 'garageSize',
  GOLF_CART_GARAGE = 'golfCartGarage',
  GOLF_CART_GARAGE_ATT_DET = 'golfCartGarageAttachedDetached',
  GOLF_CART_GARAGE_SIZE_SQ_FT = 'golfCartGarageSize',
  GREAT_ROOM = 'greatRoom',
  GREAT_ROOM_LIVING_SPACE_IMPACTS = 'greatRoomLivingSpaceImpacts',
  GREAT_ROOM_LOCATION = 'greatRoomLocation',
  GREAT_ROOM_SQ_FT = 'greatRoomSqft',
  HALF_BATHROOMS = 'halfBathrooms',
  HIDDEN = 'hidden',
  HIERARCHY = 'hierarchy',
  IMAGES = 'images',
  IMPACT_WINDOWS = 'impactWindows',
  INDOOR_FIREPLACE = 'indoorFireplace',
  INTERIOR_SPRINKLERS = 'interiorSprinklers',
  INTERIOR_WALL_CONSTRUCTION = 'interiorWallConstruction',
  IS_SYNCED_WITH_JDE = 'isSyncedWithJDE',
  LANAI = 'lanai',
  LANAI_ENCLOSURES = 'lanaiEnclosures',
  LANAI_SQ_FT = 'lanaiSqft',
  LANAI_TYPE = 'lanaiEnclosureType',
  LENNAR_URL = 'lennarUrl',
  LIVING_AREAS = 'livingAreas',
  LOFT = 'loft',
  LOFT_LIVING_SPACE_IMPACTS = 'loftLivingSpaceImpacts',
  LOFT_LOCATION = 'loftLocation',
  LOFT_SQ_FT = 'loftSqft',
  MARKETING_NAME = 'marketingName',
  MASTER_BEDROOM_LOCATION = 'masterBedroomLocation',
  MATERIAL_ROOF_TYPE = 'materialRoofType',
  MENU = 'menu',
  MODIFIED_ON_DATE = 'modifiedOnDate',
  NINTH_FLOOR_SQ_FT = 'ninthFloorSqft',
  NUMBER_OF_CARS_GARAGES = 'numberOfCarGarages',
  NUMBER_OF_CARS_GARAGES_2 = 'numberOfCarGarages2',
  NUMBER_OF_DINING_AREAS = 'numberOfDiningAreas',
  NUMBER_OF_FIXTURES = 'numberOfFixtures',
  NUMBER_OF_INDOOR_FIREPLACES = 'numberOfIndoorFireplaces',
  NUMBER_OF_OUTDOOR_FIREPLACES = 'numberOfOutdoorFireplaces',
  OPTIONAL_GARAGE = 'optionalGarage',
  OPTIONAL_GARAGE_ATT_DET = 'optionalGarageType',
  OPTIONAL_GARAGE_NUMBER_OF_CAR_GARAGES = 'optionalGarageNumberOfCarGarages',
  OPTIONAL_GARAGE_SIZE_SQ_FT = 'optionalGarageSize',
  OUTDOOR_FIREPLACE = 'outdoorFireplace',
  OUTDOOR_KITCHEN = 'outdoorKitchen',
  OWNERS_SUITE_LOCATION = 'OwnersSuiteLocation',
  PARENT_PLAN_NAME = 'parentPlanName',
  PATIO = 'patio',
  PATIO_SIZE_SQ_FT = 'patioSize',
  PATIO_TYPE = 'patioCovering',
  PLAN_CATEGORY = 'planCategory',
  PLAN_CHANGE_NUMBER = 'changeNumber',
  PLAN_CREATED_BY = 'planCreatedBy',
  PLAN_CREATED_DATE = 'planCreatedDateTime',
  PLAN_ELEVATION = 'planElevation',
  PLAN_FIELD = 'plan',
  PLAN_MODIFIED_DATE = 'planModifiedDateTime',
  PLAN_NAME = 'planName',
  PLAN_OWNED_BY_LENNAR = 'planOwnedByLennar',
  PLAN_STATUS = 'planStatus',
  PLAN_TYPE = 'planType',
  PROFIT_PERCENTAGE = 'profitPercentage',
  REGION = 'region',
  REGIONAL_PLAN_APPROVAL_STATUS = 'regionalPlanApprovalStatus',
  ROOF_SHAPE = 'roofShape',
  RV_GARAGE = 'rvGarage',
  RV_GARAGE_ATT_DET = 'rvGarageAttachedDetached',
  RV_GARAGE_SIZE_SQ_FT = 'rvGarageSize',
  SECOND_FLOOR_SQ_FT = 'secondFloorSqft',
  SERIES = 'series',
  SEVENTH_FLOOR_SQ_FT = 'seventhFloorSqft',
  SHARED_BY = 'sharedBy',
  SIDING_LAP = 'sidingLap',
  SIDING_PANEL = 'sidingPanel',
  SIXTH_FLOOR_SQ_FT = 'sixthFloorSqft',
  SOURCE_FILE = 'sourceFile',
  SQFT_HOME = 'homeSqft',
  SQFT_OF_HOME_CONDITIONED_SPACE = 'sqftOfHomeConditionedSpace',
  STORIES = 'stories',
  STORM_SHUTTERS = 'stormShutters',
  STRUCTURE_TYPE = 'structureType',
  STUDY = 'study',
  STUDY_LIVING_SPACE_IMPACTS = 'studyLivingSpaceImpacts',
  STUDY_LOCATION = 'studyLocation',
  STUDY_SQ_FT = 'studySqft',
  SUN_ROOM = 'sunRoom',
  SUN_ROOM_LIVING_SPACE_IMPACTS = 'sunRoomLivingSpaceImpacts',
  SUN_ROOM_LOCATION = 'sunRoomLocation',
  SUN_ROOM_SQ_FT = 'sunRoomSqft',
  SUPPLEMENTAL_DOCUMENTS = 'Supplemental Documents',
  SUPPORTING_DOCUMENTS = 'supportingDocuments',
  TARGET_PROFIT_PERCENTAGE = 'targetProfitPercentage',
  THIRD_FLOOR_SQ_FT = 'thirdFloorSqft',
  UNDER_ROOF_SQ_FT = 'sqftOfHomeUnderRoof',
  VALUE_ENGINEERED = 'valueEngineered',
  VAULTED_CEILING = 'vaultedCeiling',
  VAULTED_CEILING_LIVING_SPACE_IMPACTS = 'vaultedCeilingLivingSpaceImpacts',
  VAULTED_CEILING_LOCATION = 'vaultedCeilingLocation',
  WIDTH_FT = 'widthOfThePlanInFeet',
  WIDTH_IN = 'widthOfThePlanInInch',
  WIDTH_OF_PLAN = 'widthOfPlan',
  YEAR_OF_CREATION = 'yearOfCreation',
}

export enum shellFieldNames {
  ARCHITECTURAL_SHAKE = 'architecturalShake',
  ARCHITECTURAL_SHINGLE = 'architecturalShingle',
  BOARD_AND_BATTEN = 'boardAndBatten',
  BUILDING_DEPTH_FT = 'buildingDepthFt',
  BUILDING_DEPTH_IN = 'buildingDepthIn',
  BUILDING_SQ_FT = 'buildingSqFt',
  BUILDING_UNDER_ROOF_SQ_FT = 'underRoofSqFt',
  BUILDING_WIDTH_FT = 'buildingWidthFt',
  BUILDING_WIDTH_IN = 'buildingWidthIn',
  CLASS = 'class',
  DEPTH_BUILDING_FT = 'buildingDepthFeet',
  DEPTH_BUILDING_IN = 'buildingDepthInch',
  DERIVED_FROM_CORE = 'derivedFromCore',
  EXTERIOR_CLADDING = 'exteriorCladding',
  EXTERIOR_WALL_CONSTRUCTION = 'exteriorWallConstruction',
  FOUNDATION = 'foundationType',
  GARAGE_ATT_DET = 'garageType',
  GARAGE_LOAD = 'garageLoad',
  GARAGE_SIZE_2 = 'garageSize2',
  GARAGE_SIZE_SQ_FT = 'garageSize',
  INTERIOR_WALL_CONSTRUCTION = 'interiorWallConstruction',
  MARKETING_NAME = 'marketingName',
  NUMBER_OF_CARS_GARAGES = 'numberOfCarGarages',
  NUMBER_OF_CARS_GARAGES_2 = 'numberOfCarGarages2',
  SHELL_ELEVATION = 'shellElevation',
  SHELL_STATUS = 'shellStatus',
  SHELL_TYPE = 'shellType',
  SIDING_LAP = 'sidingLap',
  SIDING_PANEL = 'sidingPanel',
  STORIES = 'stories',
  UNITS = 'units',
  WIDTH_BUILDING_FT = 'buildingWidthFeet',
  WIDTH_BUILDING_IN = 'buildingWidthInch',
  YEAR_OF_CREATION = 'yearOfCreation',
}

export enum unitFieldNames {
  BASE_UNIT_PRE = 'baseUnitPre',
  BASE_UNIT_PRICE = 'baseUnitPrice',
  BASEMENT = 'basement',
  BASEMENT_SQ_FT = 'basementSqft',
  BASEMENT_TYPE = 'basementType',
  BEDROOMS = 'bedrooms',
  BONUS_ROOM = 'bonusRoom',
  BONUS_ROOM_LIVING_SPACE_IMPACTS = 'bonusRoomLivingSpaceImpacts',
  BONUS_ROOM_LOCATION = 'bonusRoomLocation',
  BONUS_ROOM_SQ_FT = 'bonusRoomSqft',
  BUILDING_SQ_FT = 'buildingSqFt',
  CLASS = 'class',
  DECK = 'deck',
  DECK_SIZE_SQ_FT = 'deckSize',
  DEPTH_UNIT_FT = 'unitDepthFeet',
  DEPTH_UNIT_IN = 'unitDepthInch',
  EFFECTIVE_DATE = 'effectiveDate',
  ENGINEER_NAME = 'engineerName',
  ENTRY = 'entry',
  ENTRY_SIZE_SQ_FT = 'entrySqft',
  EXTERIOR_CLADDING = 'exteriorCladding',
  EXTERIOR_WALL_CONSTRUCTION = 'exteriorWallConstruction',
  FAMILY_ROOM = 'familyRoom',
  FAMILY_ROOM_LIVING_SPACE_IMPACTS = 'familyRoomLivingSpaceImpacts',
  FAMILY_ROOM_LOCATION = 'familyRoomLocation',
  FAMILY_ROOM_SQ_FT = 'familyRoomSqft',
  FIRM_NAME = 'firmName',
  FLEX = 'flex',
  FLEX_LIVING_SPACE_IMPACTS = 'flexLivingSpaceImpacts',
  FLEX_LOCATION = 'flexLocation',
  FLEX_SQ_FT = 'flexSqft',
  FORMAL_DINING_ROOM = 'formalDiningRoom',
  FORMAL_LIVING_ROOM = 'formalLivingRoom',
  FOUNDATION_TYPE = 'foundationType',
  FULL_BATHROOMS = 'fullBathrooms',
  GAME_ROOM = 'gameRoom',
  GAME_ROOM_LIVING_SPACE_IMPACTS = 'gameRoomLivingSpaceImpacts',
  GAME_ROOM_LOCATION = 'gameRoomLocation',
  GAME_ROOM_SQ_FT = 'gameRoomSqft',
  GARAGE_ATT_DET = 'garageType',
  GARAGE_LOAD = 'garageLoad',
  GARAGE_SIZE_2 = 'garageSize2',
  GARAGE_SIZE_SQ_FT = 'garageSize',
  GREAT_ROOM = 'greatRoom',
  GREAT_ROOM_LIVING_SPACE_IMPACTS = 'greatRoomLivingSpaceImpacts',
  GREAT_ROOM_LOCATION = 'greatRoomLocation',
  GREAT_ROOM_SQ_FT = 'greatRoomSqft',
  HALF_BATHROOMS = 'halfBathrooms',
  IMPACT_WINDOWS = 'impactWindows',
  INDOOR_FIREPLACE = 'indoorFireplace',
  INTERIOR_SPRINKLERS = 'interiorSprinklers',
  INTERIOR_WALL_CONSTRUCTION = 'interiorWallConstruction',
  LANAI = 'lanaiEnclosures',
  LANAI_SQ_FT = 'lanaiSqft',
  LANAI_TYPE = 'lanaiEnclosureType',
  LOFT = 'loft',
  LOFT_LIVING_SPACE_IMPACTS = 'loftLivingSpaceImpacts',
  LOFT_LOCATION = 'loftLocation',
  LOFT_SQ_FT = 'loftSqft',
  MARKETING_NAME = 'marketingName',
  NUMBER_OF_CARS_GARAGES = 'numberOfCarGarages',
  NUMBER_OF_CARS_GARAGES_2 = 'numberOfCarGarages2',
  NUMBER_OF_DINING_AREAS = 'numberOfDiningAreas',
  NUMBER_OF_INDOOR_FIREPLACES = 'numberOfIndoorFireplaces',
  NUMBER_OF_LIVING_AREAS = 'livingAreas',
  NUMBER_OF_OUTDOOR_FIREPLACES = 'numberOfOutdoorFireplaces',
  OUTDOOR_FIREPLACE = 'outdoorFireplace',
  OUTDOOR_KITCHEN = 'outdoorKitchen',
  OWNERS_SUITE_LOCATION = 'masterBedroomLocation',
  PATIO = 'patio',
  PATIO_SIZE_SQ_FT = 'patioSize',
  PATIO_TYPE = 'patioCovering',
  SERIES = 'series',
  SQFT_OF_UNIT_CONDITIONED_SPACE = 'sqftOfUnitConditionedSpace',
  SQFT_UNIT = 'unitSqft',
  STORIES = 'stories',
  STORM_SHUTTERS = 'stormShutters',
  STUDY = 'study',
  STUDY_LIVING_SPACE_IMPACTS = 'studyLivingSpaceImpacts',
  STUDY_LOCATION = 'studyLocation',
  STUDY_SQ_FT = 'studySqft',
  SUN_ROOM = 'sunRoom',
  SUN_ROOM_LIVING_SPACE_IMPACTS = 'sunRoomLivingSpaceImpacts',
  SUN_ROOM_LOCATION = 'sunRoomLocation',
  SUN_ROOM_SQ_FT = 'sunRoomSqft',
  UNDER_ROOF_SQ_FT = 'sqftOfUnitUnderRoof',
  UNIT_CHANGE_NUMBER = 'changeNumber',
  UNIT_ELEVATION = 'shellElevation',
  UNIT_OWNED_BY_LENNAR = 'unitOwnedByLennar',
  UNIT_STATUS = 'unitStatus',
  UNIT_TYPE = 'unitType',
  VALUE_ENGINEERED = 'valueEngineered',
  VAULTED_CEILING = 'vaultedCeiling',
  VAULTED_CEILING_LIVING_SPACE_IMPACTS = 'vaultedCeilingLivingSpaceImpacts',
  VAULTED_CEILING_LOCATION = 'vaultedCeilingLocation',
  WIDTH_UNIT_FT = 'unitWidthFeet',
  WIDTH_UNIT_IN = 'unitWidthInch',
  YEAR_OF_CREATION = 'yearOfCreation',
}

const requiredUnitFieldKeys: (keyof typeof unitFieldNames)[] = [
  'BASE_UNIT_PRE',
  'BASE_UNIT_PRICE',
  'BASEMENT',
  'BASEMENT_SQ_FT',
  'BASEMENT_TYPE',
  'BEDROOMS',
  'BONUS_ROOM',
  'BONUS_ROOM_LIVING_SPACE_IMPACTS',
  'BONUS_ROOM_LOCATION',
  'BONUS_ROOM_SQ_FT',
  'BUILDING_SQ_FT',
  'CLASS',
  'DECK',
  'DECK_SIZE_SQ_FT',
  'DEPTH_UNIT_FT',
  'DEPTH_UNIT_IN',
  'EFFECTIVE_DATE',
  'ENTRY',
  'ENTRY_SIZE_SQ_FT',
  'EXTERIOR_CLADDING',
  'EXTERIOR_WALL_CONSTRUCTION',
  'FAMILY_ROOM',
  'FAMILY_ROOM_LIVING_SPACE_IMPACTS',
  'FAMILY_ROOM_LOCATION',
  'FAMILY_ROOM_SQ_FT',
  'FLEX',
  'FLEX_LIVING_SPACE_IMPACTS',
  'FLEX_LOCATION',
  'FLEX_SQ_FT',
  'FORMAL_DINING_ROOM',
  'FORMAL_LIVING_ROOM',
  'FOUNDATION_TYPE',
  'FULL_BATHROOMS',
  'GAME_ROOM',
  'GAME_ROOM_LIVING_SPACE_IMPACTS',
  'GAME_ROOM_LOCATION',
  'GAME_ROOM_SQ_FT',
  'GARAGE_ATT_DET',
  'GARAGE_LOAD',
  'GARAGE_SIZE_SQ_FT',
  'GARAGE_SIZE_2',
  'GREAT_ROOM',
  'GREAT_ROOM_LIVING_SPACE_IMPACTS',
  'GREAT_ROOM_LOCATION',
  'GREAT_ROOM_SQ_FT',
  'HALF_BATHROOMS',
  'IMPACT_WINDOWS',
  'INDOOR_FIREPLACE',
  'INTERIOR_SPRINKLERS',
  'LANAI',
  'LANAI_SQ_FT',
  'LANAI_TYPE',
  'LOFT',
  'LOFT_LIVING_SPACE_IMPACTS',
  'LOFT_LOCATION',
  'LOFT_SQ_FT',
  'MARKETING_NAME',
  'NUMBER_OF_CARS_GARAGES',
  'NUMBER_OF_CARS_GARAGES_2',
  'NUMBER_OF_DINING_AREAS',
  'NUMBER_OF_INDOOR_FIREPLACES',
  'NUMBER_OF_LIVING_AREAS',
  'NUMBER_OF_OUTDOOR_FIREPLACES',
  'OUTDOOR_FIREPLACE',
  'OUTDOOR_KITCHEN',
  'OWNERS_SUITE_LOCATION',
  'PATIO',
  'PATIO_SIZE_SQ_FT',
  'PATIO_TYPE',
  'SQFT_OF_UNIT_CONDITIONED_SPACE',
  'SQFT_UNIT',
  'STORIES',
  'STORM_SHUTTERS',
  'STUDY',
  'STUDY_LIVING_SPACE_IMPACTS',
  'STUDY_LOCATION',
  'STUDY_SQ_FT',
  'SUN_ROOM',
  'SUN_ROOM_LIVING_SPACE_IMPACTS',
  'SUN_ROOM_LOCATION',
  'SUN_ROOM_SQ_FT',
  'UNDER_ROOF_SQ_FT',
  'UNIT_ELEVATION',
  'UNIT_OWNED_BY_LENNAR',
  'UNIT_STATUS',
  'UNIT_TYPE',
  'VALUE_ENGINEERED',
  'VAULTED_CEILING',
  'VAULTED_CEILING_LIVING_SPACE_IMPACTS',
  'VAULTED_CEILING_LOCATION',
  'WIDTH_UNIT_FT',
  'WIDTH_UNIT_IN',
  'YEAR_OF_CREATION',
]

export const sliderFieldsToConvertToFeetAndInchesNotation: string[] = [
  'widthOfPlan',
  'depthOfPlan',
  'widthOfUnitInFeet',
  'depthOfUnitInFeet',
  'buildingWidthFt',
  'buildingDepthFt',
]

export const fieldsToCheck: string[] = Object.values(fieldNames)
export const fieldsToCheckForShell: string[] = Object.values(shellFieldNames)
export const fieldsToCheckForUnit: string[] = requiredUnitFieldKeys.map(
  (key) => unitFieldNames[key]
)

export const floorFieldNames = [
  fieldNames.FIRST_FLOOR_SQ_FT,
  fieldNames.SECOND_FLOOR_SQ_FT,
  fieldNames.THIRD_FLOOR_SQ_FT,
  fieldNames.FOURTH_FLOOR_SQ_FT,
  fieldNames.FIFTH_FLOOR_SQ_FT,
  fieldNames.SIXTH_FLOOR_SQ_FT,
  fieldNames.SEVENTH_FLOOR_SQ_FT,
  fieldNames.EIGHTH_FLOOR_SQ_FT,
  fieldNames.NINTH_FLOOR_SQ_FT,
]

export const additionalRooms: { key: string; label: string }[] = [
  {
    key: fieldNames.ADDITIONAL_BATHROOMS,
    label: COLUMN_LABELS.ADDITIONAL_BATHROOMS,
  },
  { key: fieldNames.BONUS_ROOM, label: COLUMN_LABELS.BONUS_ROOM },
  { key: fieldNames.FAMILY_ROOM, label: COLUMN_LABELS.FAMILY_ROOM },
  { key: fieldNames.FLEX, label: COLUMN_LABELS.FLEX },
  { key: fieldNames.GAME_ROOM, label: COLUMN_LABELS.GAME_ROOM },
  { key: fieldNames.GREAT_ROOM, label: COLUMN_LABELS.GREAT_ROOM },
  { key: fieldNames.LOFT, label: COLUMN_LABELS.LOFT },
  { key: fieldNames.STUDY, label: COLUMN_LABELS.STUDY },
  { key: fieldNames.SUN_ROOM, label: COLUMN_LABELS.SUN_ROOM },
]

export const optionalFieldsMap: { [key: string]: string } = {
  [fieldNames.BONUS_ROOM_LIVING_SPACE_IMPACTS]: fieldNames.BONUS_ROOM_SQ_FT,
  [fieldNames.FAMILY_ROOM_LIVING_SPACE_IMPACTS]: fieldNames.FAMILY_ROOM_SQ_FT,
  [fieldNames.FLEX_LIVING_SPACE_IMPACTS]: fieldNames.FLEX_SQ_FT,
  [fieldNames.GAME_ROOM_LIVING_SPACE_IMPACTS]: fieldNames.GAME_ROOM_SQ_FT,
  [fieldNames.GREAT_ROOM_LIVING_SPACE_IMPACTS]: fieldNames.GREAT_ROOM_SQ_FT,
  [fieldNames.LOFT_LIVING_SPACE_IMPACTS]: fieldNames.LOFT_SQ_FT,
  [fieldNames.STUDY_LIVING_SPACE_IMPACTS]: fieldNames.STUDY_SQ_FT,
  [fieldNames.SUN_ROOM_LIVING_SPACE_IMPACTS]: fieldNames.SUN_ROOM_SQ_FT,
}

export const livingSpaceImpactsMap: { [key: string]: string } = {
  [fieldNames.BONUS_ROOM]: fieldNames.BONUS_ROOM_LIVING_SPACE_IMPACTS,
  [fieldNames.FAMILY_ROOM]: fieldNames.FAMILY_ROOM_LIVING_SPACE_IMPACTS,
  [fieldNames.FLEX]: fieldNames.FLEX_LIVING_SPACE_IMPACTS,
  [fieldNames.GAME_ROOM]: fieldNames.GAME_ROOM_LIVING_SPACE_IMPACTS,
  [fieldNames.GREAT_ROOM]: fieldNames.GREAT_ROOM_LIVING_SPACE_IMPACTS,
  [fieldNames.LOFT]: fieldNames.LOFT_LIVING_SPACE_IMPACTS,
  [fieldNames.STUDY]: fieldNames.STUDY_LIVING_SPACE_IMPACTS,
  [fieldNames.SUN_ROOM]: fieldNames.SUN_ROOM_LIVING_SPACE_IMPACTS,
  [fieldNames.VAULTED_CEILING]: fieldNames.VAULTED_CEILING_LIVING_SPACE_IMPACTS,
}

export const floorMap: { [key: string]: string } = {
  [fieldNames.BONUS_ROOM]: fieldNames.BONUS_ROOM_LOCATION,
  [fieldNames.FAMILY_ROOM]: fieldNames.FAMILY_ROOM_LOCATION,
  [fieldNames.FLEX]: fieldNames.FLEX_LOCATION,
  [fieldNames.GAME_ROOM]: fieldNames.GAME_ROOM_LOCATION,
  [fieldNames.GREAT_ROOM]: fieldNames.GREAT_ROOM_LOCATION,
  [fieldNames.LOFT]: fieldNames.LOFT_LOCATION,
  [fieldNames.STUDY]: fieldNames.STUDY_LOCATION,
  [fieldNames.SUN_ROOM]: fieldNames.SUN_ROOM_LOCATION,
  [fieldNames.VAULTED_CEILING]: fieldNames.VAULTED_CEILING_LOCATION,
}

export const PLANS_TABS = {
  ALL_PLANS: 0,
  DRAFTS: 1,
  FAVORITES: 2,
  SHARED_WITH_ME: 3,
}

export enum headerNames {
  DERIVED_FROM_CORE = 'Derived from Core',
  HIERARCHY = 'Hierarchy',
}

export enum hierarchyTypes {
  CHILD = 'Child',
  GRANDCHILD = 'Grandchild',
  PARENT = 'Parent',
}

export enum planTabs {
  AdditionalOptions = 2,
  Master = 0,
  Review = 4,
  Specifications = 1,
  SupplementalDocuments = 3,
}

export enum shellTabs {
  ShellMaster = 0,
  ShellReview = 3,
  ShellSpecifications = 1,
  ShellSupplementalDocuments = 2,
}

export enum DerivedFromCoreTypes {
  NO = 'N',
  YES = 'Y',
}

export const errorMessages = {
  fileUpload: {
    emptyFile: (fileName: string): string =>
      `"${fileName}" is empty (0 KB) and can't be uploaded.`,
    failureDownloadingFile: 'Failure downloading file.',
    failureUploadingFile:
      'Failure uploading file. Save the plan and reupload the file.',
    fileDownloadInProgress:
      'File download is in progress. Please wait until the download is complete.',
    fileUploadUnderway:
      'Files upload is currently underway. Please wait until the process is finished.',
    planWithThisIdExists:
      'Plan with this id already exists. Try uploading file to the exiting plan.',
  },
  images: {
    failureDownloading: 'Error downloading image',
  },
  jde: {
    planSubmittedToJde: 'Plan submitted to JDE cannot be re-saved as draft.',
    priceStatusSyncFailed:
      'We are unable to sync price attributes and cannot submit the plan. Please try again later.',
  },
  plan: {
    archEngPdfReviewWarning: (sizeInMB: number): string =>
      `Required attribute. One pdf file of at least ${sizeInMB} MB is required.`,
    copyOutsideDivisionNotAllowed:
      'Plan copy into community outside your division is not allowed',
    divisionCreateUsersNotAllowed:
      'Core plan modification by a division user is not allowed',
    errorRenamingFile: 'Error while renaming the file',
    modificationOutsideDivisionNotAllowed:
      'Plan modification outside your division is not allowed',
    planDoesNotExist: 'Plan does not exist',
    validationFailed:
      'Error validating plan information. Please fix missing required attributes below.',
  },
  somethingWentWrong: 'Something went wrong, please try again later.',
}

export const RENDERINGS_TAB_NAME = 'renderings'
export const FLOOR_PLANS_TAB_NAME = 'floorPlans'

type DocumentCategory = {
  value: string
  label: string
  initial: string
  canPropagateFiles: boolean
}
export const documentAndSpecificationCategory: Record<
  string,
  DocumentCategory
> = {
  architectureAndEngineering: {
    canPropagateFiles: true,
    initial: 'AE',
    label: 'Architecture and Engineering Plans',
    value: 'architectureAndEngineering',
  },
  floorPlans: {
    canPropagateFiles: false,
    initial: 'FP',
    label: 'Floor Plans',
    value: 'floorPlans',
  },
  marketingMaterials: {
    canPropagateFiles: true,
    initial: 'MM',
    label: 'Marketing Materials',
    value: 'marketingMaterials',
  },
  otherDocuments: {
    canPropagateFiles: true,
    initial: 'OD',
    label: 'Other Documents',
    value: 'otherDocuments',
  },
  renderings: {
    canPropagateFiles: false,
    initial: 'RS',
    label: 'Renderings',
    value: 'renderings',
  },
  specifications: {
    canPropagateFiles: true,
    initial: 'SP',
    label: 'Specifications',
    value: 'specifications',
  },
  virtualTours: {
    canPropagateFiles: false,
    initial: 'VT',
    label: 'Virtual Tours',
    value: 'virtualTours',
  },
}

export const fileTypes = {
  PDF: '(PDF)',
}

export const specificationsOrder = [
  'modsy',
  'virtualTours',
  'marketingMaterials',
]

export enum attachedDetached {
  ATTACHED = 'Attached',
  DETACHED = 'Detached',
  NONE = 'None',
}

export enum coveredUncovered {
  COVERED = 'Covered',
  UNCOVERED = 'Uncovered',
}

export enum RoomsThatCanHaveVaultedCeilings {
  DINING = 'dining',
  ENTRY = 'entry',
  FAMILY_ROOM = 'familyRoom',
  FOYER = 'foyer',
  GREAT_ROOM = 'greatRoom',
  KITCHEN = 'kitchen',
  LIVING = 'living',
  LOFT = 'loft',
  OWNERS_BATH = 'ownersBath',
  OWNERS_SUITE = 'ownersSuite',
  STUDY = 'study',
}

export const STORIES = {
  ONE_STORY: 1,
}

export enum promiseResponse {
  FULFILLED = 'fulfilled',
  REJECTED = 'rejected',
}

export const IS_AUTO_SAVE_ENABLED = 'isAutoSaveEnabled'
export const E_TAG_TRACKER = 'eTagTracker'
export const SINGLE_FAMILY_LABEL = 'Single-Family'
export const MULTI_FAMILY_LABEL = 'Multi-Family'
export const PENDING_APPROVALS_LABEL = 'Pending Approvals'
export const REGIONAL_APPROVALS_LABEL = 'Regional Plan Approvals'
export const FAVORITES_LABEL = 'My Favorite Plans'
export const EXPORTS_LABEL = 'Exports'
export const CREATE_PLAN_LABEL = 'Create Plan'
export const AUTO_EXTRACT_LABEL = 'Auto-Extract'
export const AUTO_FILL = 'Autofill'
export const PENDING_PLANS_LABEL = 'Pending Plans'

export const romanNumeralsOneToTwenty = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
  'XIII',
  'XIV',
  'XV',
  'XVI',
  'XVII',
  'XVIII',
  'XIX',
  'XX',
]

export enum fieldIds {
  boardAndBatten = 'board-and-batten',
  cancelButtonId = 'planMasterDataForm',
  exteriorCladding = 'const-type-exterior-cladding',
  exteriorWallConstruction = 'exterior-wall-construction',
  interiorWallConstruction = 'interior-wall-construction',
}

export enum labelIds {
  boardAndBatten = 'board-and-batten-label',
}

export enum planSections {
  GARAGES = 'garages',
  SHELL_AREA = 'shellArea',
  SHELL_OVERVIEW = 'shellOverview',
  SHELL_STRUCTURE = 'shellStructure',
  STRUCTURE_SPECIFICATIONS = 'structureSpecifications',
  UNIT_AREA = 'unitArea',
  UNIT_STRUCTURE = 'unitStructure',
}

export enum classNames {
  blurField = 'blur-field',
  inputFieldArea = 'input-field-area',
  radioFieldAlignChildren = 'radio-field align-children',
  tabContentInputDisable = 'tab-content-input-disable',
}

export enum buttonTexts {
  cancelButton = 'Cancel',
  editButton = 'Edit',
  proceedButton = 'Proceed',
  refreshButton = 'Refresh',
  submitButton = 'Submit',
}

export enum dashboardCards {
  myDrafts = 'myDrafts',
  myPlans = 'myPlans',
  totalCommunities = 'totalCommunities',
  totalCorePlans = 'totalCorePlans',
  totalPlans = 'totalPlans',
  totalValueEngineeredPlans = 'totalValueEngineeredPlans',
}

export enum PlanCardLabels {
  BEDROOMS = 'Bedrooms:',
  BUILDING_LIVING_SQ_FT = 'Building Living Sq Ft:',
  COMMUNITIES = 'Communities',
  COMMUNITY = 'Community',
  COMPARE = 'Compare',
  CONDITIONED_SPACE = 'Conditioned Space:',
  DEPTH_OF_PLAN = 'Depth:',
  DEPTH_OF_THE_BUILDING = 'Building Depth:',
  DIVISION = 'Division:',
  ELEVATION = 'Elevation',
  FULL_BATHROOMS = 'Full Bathrooms:',
  HALF_BATHROOMS = 'Half Bathrooms:',
  PLAN = 'Plan',
  RENDERING_NOT_AVAILABLE = 'Rendering not available',
  SELECTED = 'Selected',
  SHELL_TYPE = 'Shell Type:',
  STORIES = 'Stories:',
  TOTAL_HOME_SQ_FT = 'Total Home Sq Ft:',
  UNITS = 'Units:',
  WIDTH_OF_PLAN = 'Width:',
  WIDTH_OF_THE_BUILDING = 'Building Width:',
}

export enum groupFieldsEnum {
  COMMUNITY_NUMBER = 'communityId',
  DIVISION = 'division',
}

export enum FilterOperationType {
  CONTAINS = 'contains',
  CONTAINS_ALL = 'containsAll',
  DOES_NOT_CONTAIN = 'doesNotContain',
  ENDS_WITH = 'endsWith',
  EQUALS = 'equals',
  GREATER_THAN = 'greaterThan',
  GREATER_THAN_OR_EQUAL = 'greaterThanOrEqual',
  IN = 'in',
  IS_NOT_NULL = 'isNotNull',
  IS_NULL = 'isNull',
  LESS_THAN = 'lessThan',
  LESS_THAN_OR_EQUAL = 'lessThanOrEqual',
  NOT_EQUALS = 'notEquals',
  STARTS_WITH = 'startsWith',
  WITHIN_RANGE = 'withinRange',
}

export enum sortFieldsEnum {
  BUILDING_SQ_FT = 'buildingSqFt',
  COMMUNITY_NAME = 'communityName',
  COMMUNITY_NUMBER = 'communityId',
  DEPTH = 'depth',
  DIVISION = 'division',
  HOME_CONDITIONED_SPACE_SQ_FT = 'sqftOfHomeConditionedSpace',
  HOME_SQFT = 'homeSqft',
  PLAN_NAME = 'plan',
  REGION = 'region',
  UNIT_CONDITIONED_SPACE_SQ_FT = 'sqftOfUnitConditionedSpace',
  UNIT_SQFT = 'unitSqft',
  WIDTH = 'width',
}

export enum sortOrderEnum {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum SearchAndCompareEnum {
  DISABLED = 'disabled',
  ENABLED = 'enabled',
  EVENT_NAME = 'handleSearchAndCompareUpdateEvent',
  KEY = 'searchAndCompare',
  // Note: 'storage' is the default name for listening to changes in localStorage in browser
  LOCAL_STORAGE_NAME = 'storage',
  SETTINGS_LABEL = 'Search & Compare Mode',
}

export const FilterMap = {
  'Does Not Have': FilterOperationType.DOES_NOT_CONTAIN,
  'Greater Than Or Equal': FilterOperationType.GREATER_THAN_OR_EQUAL,
  Has: FilterOperationType.CONTAINS,
  Is: FilterOperationType.EQUALS,
  'Is Any Of': FilterOperationType.CONTAINS,
  'Is Not': FilterOperationType.NOT_EQUALS,
  'Is On Or After': FilterOperationType.GREATER_THAN_OR_EQUAL,
  'Is On Or Before': FilterOperationType.LESS_THAN_OR_EQUAL,
  'Less Than Or Equal': FilterOperationType.LESS_THAN_OR_EQUAL,
  Within: FilterOperationType.WITHIN_RANGE,
}

export const BaseSortFields = [
  { label: 'Plan Name', value: 'plan' },
  { label: 'Community Number', value: 'communityId' },
  { label: 'Community Name', value: 'communityName' },
  { label: 'Region', value: 'region' },
  { label: 'Division', value: 'division' },
  { label: 'Width', value: 'width' },
  { label: 'Depth', value: 'depth' },
]

export const ViewOnlyGroupFields = [
  { label: 'Community Number', value: 'communityId' },
  { label: 'Division', value: 'division' },
]

export const ShellSortFields = [
  ...BaseSortFields,
  { label: 'Building Sq Ft', value: 'buildingSqFt' },
]

export const UnitSortFields = [
  ...BaseSortFields,
  { label: 'Conditioned Space Sq Ft', value: 'sqftOfUnitConditionedSpace' },
  { label: 'Total Home Sq Ft', value: 'unitSqft' },
]

export const PlanSortFields = [
  ...BaseSortFields,
  { label: 'Conditioned Space Sq Ft', value: 'sqftOfHomeConditionedSpace' },
  { label: 'Total Home Sq Ft', value: 'homeSqft' },
]

export const ViewOnlySortOrder = [
  { label: 'ASC', value: 'ASC' },
  { label: 'DESC', value: 'DESC' },
]

export enum filterSidebar {
  COMPARE_PLANS = 'Compare Plans',
  SELECT_VISIBLE_FILTERS = 'Select Visible Filters',
}

export const MAX_BASE_PLANS_PER_PAGE = 12
export const MAX_ELEVATIONS_PER_BASE_PLAN = 10

export const PLAN_CONTAINER_LABEL = 'Plans'
export const PLAN_COMPARISON_TOOLTIP = 'Select 2-4 plans for comparison.'
export const PLAN_SELECTION_TOOLTIP =
  'Maximum number of plans selected to compare'
export const ADDITIONAL_PLANS_TOOLTIP = 'Plan already selected to compare'
export const PLAN_COMPARISON_TITLE = 'Plan Comparison'
export const ADD_MORE_PLANS_TITLE = 'Add More Plans'
export const COMPARE_TITLE = 'Compare'
export const PLANS_TITLE = 'Plans'
export const APPROVED_STATUS = 'Approved'
export const DENIED_STATUS = 'Denied'
export const PENDING_STATUS = 'Pending'

export const EXPORT_MODAL_TITLE = 'Export - Select format file'
export const BULK_EXPORT_MODAL_TITLE = 'Export - Select elevation export'
export const EXCEL_EXPORT = 'Excel'
export const PDF_EXPORT = 'PDF'
export const ALL_ELEVATIONS_EXPORT = 'All_Elevations'
export const CURRENT_ELEVATION_EXPORT = 'Current_Elevation'
export const SAVE_AS_PDF = 'Save As PDF'
export const SAVE_AS_EXCEL = 'Save As Excel'
export const ALL_ELEVATIONS = 'All Elevations'
export const CURRENT_ELEVATION = 'Current Elevation'
export const EXPORT_FILE_NAME = 'Export_Plans'
export const BULK_EXPORT_PLAN_FILE_NAME = 'Bulk_Export_Plans'
export const BULK_EXPORT_UNIT_FILE_NAME = 'Bulk_Export_Units'
export const BULK_EXPORT_SHELL_FILE_NAME = 'Bulk_Export_Shells'
export const EXPORT_COMPARISON_FILE_NAME = 'Comparison_Plans_Export_'
export const EXPORT_COMPARISON_HEADER = 'Comparison Plans Export'
export const EXPORT_DATA_GRID_TYPE = 'export'
export const FILTER_DATA_GRID_TYPE = 'filter'
export const COMPARE_DATA_GRID_TYPE = 'compare'

export const FIRST_FLOOR = '1st Floor'
export const SECOND_FLOOR = '2nd Floor'
export const THIRD_FLOOR = '3rd Floor'
export const FOURTH_FLOOR = '4th Floor'
export const FIFTH_FLOOR = '5th Floor'
export const SIXTH_FLOOR = '6th Floor'
export const SEVENTH_FLOOR = '7th Floor'
export const EIGHTH_FLOOR = '8th Floor'
export const NINTH_FLOOR = '9th Floor'

export const FIRST_FLOOR_DISABLED_TOOLTIP = '1st floor image isn’t available.'
export const SECOND_FLOOR_DISABLED_TOOLTIP = '2nd floor image isn’t available.'
export const REVERSE_IMAGE_DISABLED_TOOLTIP = 'Reverse image isn’t available.'
export const DEFAULT_IMAGE_DISABLED_TOOLTIP = 'Default image isn’t available.'
export const FLOOR_PLANS_FETCH_ERROR = 'Failed to Fetch Floor Plans Images'
export const FLOORPLAN_NOT_AVAILABLE_MESSAGE =
  'Floorplan not available for this plan'
export const PHOTOS_NOT_AVAILABLE_MESSAGE = 'Photos not available for this plan'
export const FLOOR_TYPE_DEFAULT = 'default'
export const FLOOR_TYPE_REVERSED = 'reversed'
export const DEFAULT_LABEL = 'Default'
export const REVERSED_LABEL = 'Reversed'
export const SHOW_REVERSED_LABEL = 'Show Reversed'
export const SUPPLEMENTAL_SPECS_DOCS = 'Supplemental Specs/Docs'
export const FLOOR_PLANS = 'Floor Plans'
export const SUPPLEMENTAL_SPECS_DOCS_HEADER = 'supplemental-specs-docs-header'
export const FLOOR_PLANS_HEADER = 'floor-plans-header'
export const VIRTUAL_TOURS_PHOTOS_HEADER = 'virtual-tours-photos'
export const VIRTUAL_TOURS_PHOTOS = 'Photos'
export const VIRTUAL_TOURS_LINKS_HEADER = 'virtual-tour'
export const VIRTUAL_TOURS_LINKS = 'Virtual Tour'
export const VIRTUAL_TOUR_IMAGE_SIZE = 200
export const THUMBNAIL_IMAGE_SIZE = 300
export const FLOORPLAN_PREFIX_LENGTH = 10

export const MODSY_SPEC_FAILED = 'Failed to retrieve specs information'
export const MODSY_SPEC_NOT_FOUND = 'Spec Does Not Exist'

export const floorNumberToConstant: Record<string, string> = {
  '1': FIRST_FLOOR,
  '2': SECOND_FLOOR,
  '3': THIRD_FLOOR,
  '4': FOURTH_FLOOR,
  '5': FIFTH_FLOOR,
  '6': SIXTH_FLOOR,
  '7': SEVENTH_FLOOR,
  '8': EIGHTH_FLOOR,
  '9': NINTH_FLOOR,
}

/* eslint-disable perfectionist/sort-objects */
export const IPLAN_PROPERTY_LABELS: Record<string, string> = {
  additionalBathrooms: 'Additional Bathrooms',
  baseHousePrice: 'Base House Price',
  baseHousePre: 'Base House Pre',
  basement: 'Basement',
  bedrooms: 'Bedrooms',
  bonusRoom: 'Bonus Room',
  depthOfThePlanInFeet: 'Depth (Feet)',
  depthOfThePlanInInch: 'Depth (Inch)',
  division: 'Division',
  exteriorCladding: 'Exterior Cladding',
  exportFloorPlans: 'Export Floor Plans',
  exteriorWallConstruction: 'Exterior Wall Construction',
  familyRoom: 'Family Room',
  flex: 'Flex Room',
  formalLivingRoom: 'Formal Living Room',
  foundationType: 'Foundation Type',
  fullBathrooms: 'Full Bathrooms',
  gameRoom: 'Game Room',
  garageAlignment: 'Garage Alignment',
  garageSize: 'Garage Size',
  garageSize2: 'Garage Size 2',
  garageType: 'Garage Type',
  greatRoom: 'Great Room',
  halfBathrooms: 'Half Bathrooms',
  homeSqft: 'Home Sqft',
  livingAreas: 'Number of Living Areas',
  loft: 'Loft',
  marketingName: 'Marketing Name',
  numberOfCarGarages: 'Number Of Car Garages',
  numberOfCarGarages2: 'Number Of Car Garages 2',
  patio: 'Patio',
  planCreatedBy: 'Plan Created By',
  planCreatedDateTime: 'Plan Created Date',
  planModifiedDateTime: 'Plan Modified Date',
  planOwnedByLennar: 'Plan Owned By Lennar',
  planStatus: 'Plan Status',
  planType: 'Plan Type',
  parentPlanName: 'Parent Plan',
  region: 'Region',
  sqftOfHomeConditionedSpace: 'Sqft Of Home Conditioned Space',
  stories: 'Stories',
  study: 'Study',
  sunRoom: 'Sun Room',
  valueEngineered: 'Value Engineered',
  widthOfThePlanInFeet: 'Width (Feet)',
  widthOfThePlanInInch: 'Width (Inch)',
  yearOfCreation: 'Year Of Creation',
}

export const IUNIT_PROPERTY_LABELS: Record<string, string> = {
  basement: 'Basement',
  baseUnitPre: 'Base Unit Pre',
  baseUnitPrice: 'Base Unit Price',
  bedrooms: 'Bedrooms',
  bonusRoom: 'Bonus Room',
  division: 'Division',
  livingAreas: 'Number of Living Areas',
  loft: 'Loft',
  region: 'Region',
  sqftOfUnitConditionedSpace: 'Sqft of Unit Conditioned Space',
  parentPlanName: 'Parent Plan',
  stories: 'Stories',
  structureType: 'Structure Type',
  study: 'Study',
  sunRoom: 'Sun Room',
  unitDepthFeet: 'Depth (Feet)',
  unitDepthInch: 'Depth (Inch)',
  unitSqft: 'Total Unit Sq Ft',
  unitType: 'Unit Type',
  unitWidthFeet: 'Width (Feet)',
  unitWidthInch: 'Width (Inch)',
  valueEngineered: 'Value Engineered',
  yearOfCreation: 'Year Of Creation',
}

export const ISHELL_PROPERTY_LABELS: Record<string, string> = {
  buildingDepthFeet: 'Building Depth (Feet)',
  buildingDepthInch: 'Building Depth (Inch)',
  buildingSqFt: 'Building Living Sq Ft.',
  underRoofSqFt: 'Building Total Under Roof Sq Ft.',
  buildingWidthFeet: 'Building Width (Feet)',
  buildingWidthInch: 'Building Width (Inch)',
  derivedFromCore: 'Derived From Core',
  division: 'Division',
  exteriorCladding: 'Exterior Cladding',
  exteriorWallConstruction: 'Exterior Wall Construction',
  foundationType: 'Foundation Type',
  garageLoad: 'Garage Load',
  garageType: 'Garage Type',
  hierarchy: 'Hierarchy',
  marketingName: 'Marketing Name',
  numberOfCarGarages: 'Number Of Car Garages',
  parentPlanName: 'Parent Plan',
  plan: 'Plan Name',
  planCreatedBy: 'Plan Created By',
  planCreatedDateTime: 'Plan Created Date',
  planModifiedDateTime: 'Plan Modified Date',
  region: 'Region',
  shellStatus: 'Shell Status',
  shellType: 'Shell Type',
  stories: 'Stories',
  structureType: 'Structure Type',
  units: 'Units',
  yearOfCreation: 'Year Of Creation',
}
/* eslint-enable perfectionist/sort-objects */
export const TOOLTIP_FOR_AIRTABLE_DATA =
  'This data is sourced from Box via Airtable and cannot be edited'
